import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Group,
  getGroup,
  listGroups as apiListGroups
} from '../../../../../services/group'
import {
  PriceList,
  getPriceList,
  listPriceLists as apiListPriceLists
} from '../../../../../services/priceList'
import { User, getUser as apiGetUser } from '../../../../../services/users'

import { UserFormatted, formatUser } from '../../../utils/formatUser'

interface UserFormContextContent {
  user?: UserFormatted
  priceList?: PriceList
  group?: Group
  loading: boolean
  setLoading: (loading: boolean) => void
  getUserState: () => UserFormatted
  updateUserState: (user: User) => void
  groups: Group[]
  loadingGroups: boolean
  listGroups: (search: string) => void
  priceLists: PriceList[]
  loadingPriceLists: boolean
  listPriceLists: (search: string) => void
}

export const UserFormContext = React.createContext<UserFormContextContent>(
  {} as UserFormContextContent
)

interface UserFormProviderProps {
  children: React.ReactNode
}

export const UserFormProvider: React.FC<UserFormProviderProps> = ({
  children
}) => {
  const [searchParams] = useSearchParams()
  const [user, setUser] = useState<UserFormatted>()
  const [priceList, setPriceList] = useState<PriceList>()
  const [group, setGroup] = useState<Group>()
  const [loading, setLoading] = useState(false)
  const [groups, setGroups] = useState<Group[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [priceLists, setPriceLists] = useState<PriceList[]>([])
  const [loadingPriceLists, setLoadingPriceLists] = useState(false)

  function updateUserState(data: User) {
    const formatted = formatUser(data)
    setUser(formatted)
  }

  function getUser(id: string) {
    Promise.resolve()
      .then(() => setLoading(true))
      .then(() => apiGetUser(id))
      .then(data => {
        if (data) return updateUserState(data)

        // navigate(`${pages.users.path}/inclusao`)
        toast.warn('O usuário não foi encontrado')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const id = searchParams.get('id')
    if (id) getUser(id)
  }, [])

  useEffect(() => {
    if (!user) return
    if (!user.priceListId) return

    Promise.resolve()
      .then(() => setLoading(true))
      .then(() => getPriceList(user.priceListId as string))
      .then(data => setPriceList(data))
      .finally(() => setLoading(false))
  }, [user])

  useEffect(() => {
    if (!user) return
    if (!user.groupId) return

    Promise.resolve()
      .then(() => setLoading(true))
      .then(() => getGroup(user.groupId as string))
      .then(data => setGroup(data))
      .finally(() => setLoading(false))
  }, [user])

  function getUserState() {
    if (!user) throw new Error('Usuário não encontrado')
    return user
  }

  function listGroups(search: string) {
    Promise.resolve()
      .then(() => setLoadingGroups(true))
      .then(() => apiListGroups({ search }))
      .then(data => setGroups(data.groups))
      .finally(() => setLoadingGroups(false))
  }

  function listPriceLists(search: string) {
    Promise.resolve()
      .then(() => setLoadingPriceLists(true))
      .then(() => apiListPriceLists({ search }))
      .then(data => setPriceLists(data.priceLists))
      .finally(() => setLoadingPriceLists(false))
  }

  return (
    <UserFormContext.Provider
      value={{
        user,
        priceList,
        group,
        loading,
        setLoading,
        getUserState,
        updateUserState,
        groups,
        loadingGroups,
        listGroups,
        priceLists,
        loadingPriceLists,
        listPriceLists
      }}
    >
      {children}
    </UserFormContext.Provider>
  )
}
